import { useRef, useEffect, type ReactNode } from "react";
import { motion } from "framer-motion";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";
import swagImage from "@/assets/captains-welcome-swag.webp";
import posterImage from "@/assets/captains-welcome-poster.webp";

/**
 * The Captain's Welcome — Saturday-only opening celebration. The first 350
 * ticket scans through the gate get the full Welcome: free breakfast in the
 * air-conditioned Captain's Galley (Noon–1), free parking, the Captain's
 * Welcome Bag, a photo with The Captain, trivia and more. Uses the same
 * poster-panel skin as /schedule and /admirals-menu.
 */

/* ── Poster palette (matches /schedule + /admirals-menu) ── */
const NAVY = "#14457F";
const BUTTER = "#FBF4BC";
const SKY = "#A9D4F5";
const GOLD = "#D9A62E";
const SPLASH = "#4FA8E0";

const PERKS: { title: string; detail: string }[] = [
  {
    title: "Free Hot Breakfast",
    detail:
      "Served Noon–1:00 PM sharp in the air-conditioned Captain's Galley. Full menu below.",
  },
  {
    title: "Free Parking",
    detail:
      "Arrive before 2:00 PM Saturday and park free at Riverside Marina — shuttle and ferry to the festival included.",
  },
  {
    title: "The Captain's Welcome Bag",
    detail:
      "Yacht Rock Detroit drawstring bag loaded with a captain's hat, retro sunglasses, Stay Smooth lip balm, sticker — plus a few surprises.",
  },
  {
    title: "Meet The Captain",
    detail:
      "Welcomed aboard by The Captain himself, with a keepsake photo to prove it.",
  },
  {
    title: "Yacht Rock Trivia",
    detail:
      "The smoothest trivia in town, hosted by DJ Zumby — prizes included.",
  },
  {
    title: "The Captain's Cocktail Bar",
    detail:
      "Bloody Marys with Absolut and fresh mimosas with La Marca Prosecco, available for purchase right next to breakfast.",
  },
];

const BREAKFAST: { name: string; detail?: string }[] = [
  { name: "Fluffy Scrambled Eggs" },
  { name: "Applewood Smoked Bacon" },
  { name: "Country Sausage Links" },
  { name: "Roasted Breakfast Potatoes", detail: "peppers & onions" },
  {
    name: "Assorted Fresh Mini Muffins",
    detail: "cinnamon roll, blueberry & chocolate chip",
  },
  { name: "Seasonal Fresh Fruit" },
];

const BEVERAGES: { name: string; detail?: string }[] = [
  { name: "Orange Juice" },
  { name: "Cranberry Juice" },
  { name: "Freshly Brewed Coffee" },
  { name: "Iced Tea" },
];

const COCKTAILS: { name: string; detail?: string }[] = [
  { name: "Bloody Marys", detail: "featuring Absolut Vodka" },
  { name: "Mimosas", detail: "La Marca Prosecco & chilled orange juice" },
];

const ENTERTAINMENT: { name: string; detail: string }[] = [
  { name: "DJ Rick Scampi", detail: "spinning yacht rock hits & classics" },
  {
    name: "Urban Tropical",
    detail: "horns, bongos, guitar, keys & tropical rhythms",
  },
  {
    name: "Yacht Rock Trivia with DJ Zumby",
    detail: "prizes, bragging rights & very smooth questions",
  },
];

/** Sticker-style sun (matches the circle-design carousel). */
const SunSticker = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden>
    <g fill="#F7B41C" stroke="#FFFFFF" strokeWidth="2.5" strokeLinejoin="round">
      {Array.from({ length: 12 }, (_, i) => (
        <path
          key={i}
          d="M32 6 C34.5 12, 29.5 12, 32 18 Z"
          transform={`rotate(${i * 30} 32 32)`}
        />
      ))}
      <circle cx="32" cy="32" r="13" />
    </g>
  </svg>
);

/** Water-splash accent (matches the circle-design carousel). */
const SplashSticker = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden>
    <path d="M14 40 C10 30, 12 24, 18 18 C18 28, 17 32, 14 40 Z" fill={NAVY} />
    <path d="M24 38 C22 30, 24 25, 29 21 C28 29, 27 32, 24 38 Z" fill={SPLASH} />
    <circle cx="10" cy="20" r="2.6" fill={NAVY} />
    <circle cx="34" cy="30" r="2.2" fill={SPLASH} />
  </svg>
);

/** Gold "rope ring" poster panel with sun + splash stickers (matches /schedule). */
const PosterPanel = ({
  tone,
  children,
}: {
  tone: "sky" | "butter";
  children: ReactNode;
}) => (
  <div
    className="rounded-[1.6rem] md:rounded-[2.2rem] p-1.5 md:p-2"
    style={{ background: tone === "sky" ? SKY : BUTTER, border: `3px solid ${GOLD}` }}
  >
    <div
      className="relative rounded-[1.15rem] md:rounded-[1.7rem] px-3.5 py-4 sm:px-5 md:px-8 md:py-7 overflow-hidden"
      style={{ border: `2px dashed ${GOLD}99` }}
    >
      <SunSticker className="absolute top-3 right-3 w-12 h-12 md:w-16 md:h-16 md:top-5 md:right-5 pointer-events-none" />
      <SplashSticker className="absolute bottom-2 left-1.5 w-10 h-10 md:w-14 md:h-14 opacity-70 pointer-events-none" />
      {children}
    </div>
  </div>
);

const MenuRow = ({ item }: { item: { name: string; detail?: string } }) => (
  <p className="text-[15px] md:text-[17px] leading-snug py-1">
    <span className="font-bold" style={{ color: NAVY }}>
      {item.name}
    </span>
    {item.detail && <span style={{ color: `${NAVY}B3` }}> — {item.detail}</span>}
  </p>
);

const CaptainsWelcome = () => {
  usePageSEO({
    title: "The Captain's Welcome — Yacht Rock Detroit 2026 | First 350 Aboard Saturday",
    description:
      "Saturday at Yacht Rock Detroit: gates open at noon and the first 350 ticket scans get the Captain's Welcome — free hot breakfast in the air-conditioned Captain's Galley, free parking, a Captain's Welcome Bag, a photo with The Captain, yacht rock trivia and a Bloody Mary bar. July 18 at The Roostertail.",
    keywords:
      "captains welcome yacht rock detroit, yacht rock detroit saturday, free breakfast festival detroit, yacht rock detroit first 350, roostertail saturday",
    ogImage: "https://yachtrockdetroit.com/og-captains-welcome.jpg",
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "The Captain's Welcome", url: "https://yachtrockdetroit.com/captains-welcome" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative bg-yacht-blue overflow-hidden pt-28 md:pt-40 pb-12 md:pb-16"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.6 }}
          >
            Saturday, July 18 · Gates at Noon · The Roostertail
          </motion.p>
          <div
            className="text-[10vw] md:text-7xl font-bold text-white leading-[0.95] tracking-tight"
            role="heading"
            aria-level={1}
          >
            <GradualSpacing
              text="The Captain's Welcome"
              duration={0.5}
              delayMultiple={0.04}
            />
          </div>
          <motion.p
            className="text-white/85 text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Every great voyage begins with a proper welcome. On Saturday, the
            first 350 ticket scans through the gate get the full Captain's
            Welcome — and once guest #350 checks in, it's gone.
          </motion.p>
          <motion.p
            className="text-yacht-yellow font-bold text-xs md:text-sm tracking-[0.22em] uppercase mt-5"
            initial={{ opacity: 0 }}
            animate={isHeaderInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            Free breakfast · Free parking · Welcome bag · First 350 only
          </motion.p>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-10 md:py-16 px-5 sm:px-6 md:px-8">
        <div className="max-w-3xl mx-auto space-y-8 md:space-y-12">
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3 md:mb-4 px-1">
              <h2
                className="font-['Anton'] uppercase tracking-wide text-3xl md:text-5xl leading-none"
                style={{ color: NAVY }}
              >
                What the First 350 Get
              </h2>
              <span
                className="text-[11px] md:text-sm font-semibold"
                style={{ color: `${NAVY}8C` }}
              >
                counted by ticket scans at the gate
              </span>
            </div>
            <PosterPanel tone="butter">
              {PERKS.map((perk, i) => (
                <div
                  key={perk.title}
                  className="relative z-10 py-4 md:py-5 border-b last:border-b-0 flex gap-3.5 md:gap-5"
                  style={{ borderColor: `${NAVY}26` }}
                >
                  <span
                    className="font-['Anton'] text-2xl md:text-4xl leading-none shrink-0 w-8 md:w-12 text-right"
                    style={{ color: GOLD }}
                  >
                    {i + 1}
                  </span>
                  <div>
                    <h3
                      className="font-['Anton'] uppercase tracking-wide text-xl md:text-3xl leading-none"
                      style={{ color: NAVY }}
                    >
                      {perk.title}
                    </h3>
                    <p
                      className="mt-1.5 text-[15px] md:text-[17px] leading-snug"
                      style={{ color: `${NAVY}B3` }}
                    >
                      {perk.detail}
                    </p>
                  </div>
                </div>
              ))}
            </PosterPanel>
          </div>

          {/* SWAG PHOTO */}
          <div className="grid md:grid-cols-2 gap-5 md:gap-8 items-center">
            <img
              src={swagImage}
              alt="The Captain's Welcome Bag — Yacht Rock Detroit drawstring bag, captain's hat, retro sunglasses, Stay Smooth lip balm and sticker"
              className="rounded-[1.6rem] md:rounded-[2.2rem] w-full"
              style={{ border: `3px solid ${GOLD}` }}
              loading="lazy"
            />
            <img
              src={posterImage}
              alt="The Captain's Welcome — Saturday, gates at noon, first 350 aboard"
              className="rounded-[1.6rem] md:rounded-[2.2rem] w-full"
              style={{ border: `3px solid ${GOLD}` }}
              loading="lazy"
            />
          </div>

          {/* BREAKFAST MENU */}
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3 md:mb-4 px-1">
              <h2
                className="font-['Anton'] uppercase tracking-wide text-3xl md:text-5xl leading-none"
                style={{ color: NAVY }}
              >
                Captain's Galley Breakfast
              </h2>
              <span
                className="text-[11px] md:text-sm font-semibold"
                style={{ color: `${NAVY}8C` }}
              >
                served Noon–1:00 PM · indoors, air-conditioned
              </span>
            </div>
            <PosterPanel tone="sky">
              <div className="relative z-10 py-2">
                {BREAKFAST.map((item) => (
                  <MenuRow key={item.name} item={item} />
                ))}
              </div>
              <div
                className="relative z-10 pt-4 mt-2 border-t"
                style={{ borderColor: `${NAVY}26` }}
              >
                <h3
                  className="font-['Anton'] uppercase tracking-wide text-lg md:text-2xl leading-none mb-1.5"
                  style={{ color: NAVY }}
                >
                  Included Beverages
                </h3>
                {BEVERAGES.map((item) => (
                  <MenuRow key={item.name} item={item} />
                ))}
              </div>
              <div
                className="relative z-10 pt-4 mt-2 border-t"
                style={{ borderColor: `${NAVY}26` }}
              >
                <h3
                  className="font-['Anton'] uppercase tracking-wide text-lg md:text-2xl leading-none mb-1.5"
                  style={{ color: NAVY }}
                >
                  Captain's Cocktail Bar
                  <span
                    className="ml-2 text-[10.5px] md:text-xs font-bold uppercase tracking-[0.14em]"
                    style={{ color: `${NAVY}8C` }}
                  >
                    available for purchase
                  </span>
                </h3>
                {COCKTAILS.map((item) => (
                  <MenuRow key={item.name} item={item} />
                ))}
              </div>
            </PosterPanel>
          </div>

          {/* ENTERTAINMENT */}
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3 md:mb-4 px-1">
              <h2
                className="font-['Anton'] uppercase tracking-wide text-3xl md:text-5xl leading-none"
                style={{ color: NAVY }}
              >
                On Deck From Noon
              </h2>
              <span
                className="text-[11px] md:text-sm font-semibold"
                style={{ color: `${NAVY}8C` }}
              >
                the party starts long before the headliners
              </span>
            </div>
            <PosterPanel tone="butter">
              {ENTERTAINMENT.map((act) => (
                <div
                  key={act.name}
                  className="relative z-10 py-4 md:py-5 border-b last:border-b-0"
                  style={{ borderColor: `${NAVY}26` }}
                >
                  <h3
                    className="font-['Anton'] uppercase tracking-wide text-xl md:text-3xl leading-none"
                    style={{ color: NAVY }}
                  >
                    {act.name}
                  </h3>
                  <p
                    className="mt-1.5 text-[15px] md:text-[17px] leading-snug"
                    style={{ color: `${NAVY}B3` }}
                  >
                    {act.detail}
                  </p>
                </div>
              ))}
            </PosterPanel>
          </div>

          {/* THE RULES */}
          <p className="text-yacht-charcoal/45 text-xs md:text-sm leading-relaxed">
            The Captain's Welcome happens once — Saturday only, Noon to 2:00 PM,
            for the first 350 ticket scans through the gate. Breakfast is served
            Noon to 1:00 PM sharp. Free Riverside Marina parking applies to
            Saturday arrivals before 2:00 PM and includes shuttle and ferry
            transportation. Menu subject to change. When the 350th guest checks
            in, the Welcome closes and Yacht Rock Detroit sets sail.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="/parking"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-yacht-charcoal text-yacht-charcoal font-bold text-base hover:bg-yacht-charcoal hover:text-yacht-cream transition-colors"
            >
              Plan Your Parking
            </a>
            <a
              href="/tickets"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base hover:brightness-110 transition-all"
              style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
            >
              Get Saturday Tickets →
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaptainsWelcome;
